/**
 * 딜등록 목록의 상태 칸 — 누르면 바로 고르는 드롭다운 (v2.215.0 · 2026-09-29)
 *
 * 사용자 지시: 「상태 (ongoing, won 등) 변경을 목록에서 그 상태 클릭하면 드롭박스로 변경할 수 있도록 해줘. 편하게 상태 바꿀수 있게」
 *
 * · 배지 모양 그대로인 **네이티브 select** — 한 번 누르면 바로 목록이 펼쳐진다(두 번 누르게 하지 않는다).
 * · 줄을 누르면 상세 창이 열리므로 **클릭이 줄로 번지지 않게** 막는다.
 * · 글자는 목록 배지와 같다 — 인포트렌드 상태는 영문 그대로, 우리가 만든 승인대기·거절은 한글.
 * · 저장은 부르는 쪽(`onChange`)이 한다 — 서버 `PATCH /deal-registrations/:id/status`.
 *   ⚠종료 상태로 바꿔도 **종료 탭으로 넘어가지 않는다**(넘기기는 [종료 탭으로 넘기기]).
 */

import { STATUS_BADGE } from '../lib/deal-display';
import type { DealRow, DealStatus } from '../lib/deal-registration-api';

/** 고르는 차례 — 목록 정렬 차례와 같다(진행 → 종료). ⭐여덟 가지를 다 적을 것 */
export const STATUS_CHOICES: DealStatus[] = ['Pending', 'On-going', 'Extend', 'Processing', 'Won', 'Lost', 'Invalid', 'Rejected'];

/** 배지·드롭다운에 보이는 글자 */
export function statusShort(s: DealStatus): string {
  if (s === 'Pending') return '승인대기';
  if (s === 'Rejected') return '거절';
  return s;
}

/*
 * ⚠목록 글자에 「(진행)」 같은 한글 뜻을 붙이지 않는다 — 닫힌 select 는 **고른 줄의 글자를 그대로** 보여 줘서
 *   칸(78px)이 「On-going (진행)」 으로 넓어진다. 배지와 같은 글자만 쓴다.
 */

export function DealStatusSelect({
  row,
  busy,
  onChange,
}: {
  row: Pick<DealRow, 'id' | 'status'>;
  busy: boolean;
  onChange: (s: DealStatus) => void;
}): React.JSX.Element {
  return (
    <select
      value={row.status}
      disabled={busy}
      aria-label="상태 바꾸기"
      title="눌러서 상태 바꾸기 — 종료 상태로 바꿔도 진행 중 탭에 남습니다"
      data-deal-status-select={row.id}
      // 줄을 누르면 상세 창이 열린다 — 드롭다운을 누른 것은 줄 클릭이 아니다
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      onChange={(e) => {
        const next = e.target.value as DealStatus;
        if (next !== row.status) onChange(next);
      }}
      className={`appearance-none cursor-pointer rounded font-semibold text-xs pl-1.5 pr-4 py-0.5 border border-transparent
        hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-300 disabled:opacity-50 disabled:cursor-wait
        bg-no-repeat bg-[right_3px_center] bg-[length:8px_8px] max-w-full
        ${STATUS_BADGE[row.status] ?? 'bg-slate-100'}`}
      style={{
        // 작은 ▾ — 배지 오른쪽 끝(글자와 겹치지 않게 pr-4)
        backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 8 8'%3E%3Cpath d='M0 2l4 4 4-4z' fill='%23475569'/%3E%3C/svg%3E\")",
      }}
    >
      {STATUS_CHOICES.map((s) => (
        <option key={s} value={s} className="bg-white text-slate-800 font-normal">
          {statusShort(s)}
        </option>
      ))}
    </select>
  );
}
